import dayjs from 'dayjs';
import { TFunction } from 'i18next';

import { MemoryDatePrecision } from 'src/Services/API/Memories.api';

export type MemorySeason = 'SPRING' | 'SUMMER' | 'AUTUMN' | 'WINTER';

// Pivot month (0-based) of each season, mirrored from the API's normalisation.
export const SEASON_MONTH: Record<MemorySeason, number> = {
  WINTER: 0,
  SPRING: 3,
  SUMMER: 6,
  AUTUMN: 9,
};

const seasonOfMonth = (month: number): MemorySeason => {
  if (month <= 2) return 'WINTER';
  if (month <= 5) return 'SPRING';
  if (month <= 8) return 'SUMMER';
  return 'AUTUMN';
};

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

/**
 * Human label of a memory date: « Samedi 13 juin 1987 », « Juin 1987 », « Été 1992 »,
 * « 1962 », « Années 70 », prefixed with « Vers » when approximate. Dates are calendar dates
 * stored at UTC midnight, so they are read in UTC to never shift by a day.
 */
export const formatMemoryDate = (
  t: TFunction,
  takenAt: string | null,
  precision: MemoryDatePrecision,
  approximate: boolean
): string | null => {
  if (!takenAt) return null;
  const date = dayjs.utc(takenAt);
  const year = date.year();

  let label: string;
  switch (precision) {
    case MemoryDatePrecision.Day:
      label = capitalize(date.format('dddd D MMMM YYYY'));
      break;
    case MemoryDatePrecision.Month:
      label = capitalize(date.format('MMMM YYYY'));
      break;
    case MemoryDatePrecision.Season:
      label = t(`memories.seasons.${seasonOfMonth(date.month())}`, {
        year,
        defaultValue: '{{year}}',
      });
      break;
    case MemoryDatePrecision.Year:
      label = String(year);
      break;
    case MemoryDatePrecision.Decade:
      label = decadeLabel(t, Math.floor(year / 10) * 10);
      break;
  }

  return approximate ? t('memories.approximately', 'Vers {{label}}', { label }) : label;
};

/** « Années 70 » for the last century, « Années 2020 » from 2000 on (« Années 20 » reads as 1920). */
export const decadeLabel = (t: TFunction, decade: number): string =>
  t('memories.decadeLabel', 'Années {{decade}}', {
    decade: decade < 2000 ? String(decade).slice(-2) : String(decade),
  });

/** Decade of a memory (1987 → 1980), or null when undated. */
export const decadeOf = (takenAt: string | null): number | null =>
  takenAt ? Math.floor(dayjs.utc(takenAt).year() / 10) * 10 : null;

export const formatVoiceDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const rest = String(seconds % 60).padStart(2, '0');
  return `${minutes}:${rest}`;
};
